import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { CreditCardIcon, EyeIcon, DocumentArrowDownIcon, CheckIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { usePaginatedApi } from '../hooks/useApi';
import { paymentsAPI, clientsAPI } from '../services/api';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchableSelect from '../components/SearchableSelect';
import { paymentVerifierDisplayName } from '../utils/helpers';
import {
  getBillPaymentsTimelineChipsUpToRow,
  paymentStatusI18nKey,
  shouldHideSupersededFailedPaymentRow
} from '../utils/paymentStatusI18n';

const PaymentsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const openPaymentFromNavHandledRef = useRef(null);
  const { isAccountant, isClient } = useAuth();
  const toast = useToast();
  const paymentsPageDescription = isAccountant()
    ? t('payments.paymentsDescription')
    : isClient()
      ? t('payments.paymentsDescriptionClient')
      : t('payments.paymentsDescriptionView');

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    clientId: '',
    status: '',
    paymentMethod: '',
    startDate: '',
    endDate: ''
  });

  // Modal states
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [paymentToApprove, setPaymentToApprove] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);

  /** Payments list: hide completed by default; checkbox sends includeCompleted to API. */
  const [showCompletedPayments, setShowCompletedPayments] = useState(false);
  const [paymentFiltersOpen, setPaymentFiltersOpen] = useState(false);

  const withListParams = useCallback((base, completedOverride) => {
    const want =
      typeof completedOverride === 'boolean' ? completedOverride : showCompletedPayments;
    return { ...base, includeCompleted: want ? 'true' : 'false' };
  }, [showCompletedPayments]);

  // API hooks
  const {
    data: payments,
    pagination,
    loading: paymentsLoading,
    error: paymentsError,
    updateParams,
    goToPage,
    refetch: refetchPayments
  } = usePaginatedApi(paymentsAPI.getAll, filters);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const skipInitialSearchDebounceRef = useRef(true);
  const searchDebounceRef = useRef(null);
  useEffect(() => {
    if (skipInitialSearchDebounceRef.current) {
      skipInitialSearchDebounceRef.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      updateParams(withListParams({ ...filtersRef.current }));
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [filters.search, updateParams, withListParams]);

  /**
   * Hide rejected payment rows once a newer non-failed payment exists on the same bill (client re-submitted).
   * When filtering explicitly to FAILED, show every failed row (including superseded) for audit.
   */
  const paymentsForTable = useMemo(() => {
    const list = Array.isArray(payments) ? payments : [];
    if (filters.status === 'FAILED') return list;
    return list.filter((row) => !shouldHideSupersededFailedPaymentRow(row, row.bill?.payments));
  }, [payments, filters.status]);

  // Get clients for filter dropdown
  const { data: clientsData, loading: clientsLoading } = usePaginatedApi(clientsAPI.getAll, { limit: 100 });

  const clientFilterOptions = useMemo(
    () => {
      const clients = Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []);
      return [
        { value: '', label: t('certificates.allClients') },
        ...clients.map((c) => ({ value: c.id, label: c.companyName || '—' })),
      ];
    },
    [clientsData, t]
  );



  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-update for non-search fields, but not for search
    if (field !== 'search') {
      updateParams(withListParams({
        ...filters,
        [field]: value
      }));
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      clientId: '',
      status: '',
      paymentMethod: '',
      startDate: '',
      endDate: ''
    };
    setFilters(clearedFilters);
    setShowCompletedPayments(false);
    updateParams(withListParams(clearedFilters, false));
  };

  const handleShowCompletedPaymentsChange = (e) => {
    const checked = Boolean(e.target.checked);
    setShowCompletedPayments(checked);
    updateParams(withListParams({ ...filters, page: 1 }, checked));
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentModal(true);
  };

  // Open payment modal when navigating from another page (e.g. bill details)
  useEffect(() => {
    const openPaymentId = location.state?.openPaymentId;
    if (!openPaymentId) {
      openPaymentFromNavHandledRef.current = null;
      return;
    }
    if (openPaymentFromNavHandledRef.current === openPaymentId) return;
    openPaymentFromNavHandledRef.current = openPaymentId;

    navigate(location.pathname, { replace: true, state: {} });

    (async () => {
      try {
        const response = await paymentsAPI.getById(openPaymentId);
        setSelectedPayment(response.data.payment);
        setShowPaymentModal(true);
      } catch (err) {
        console.error('Error opening payment from navigation:', err);
        toast.error(err?.response?.data?.message || err?.message || t('common.error'));
      }
    })();
  }, [location.state?.openPaymentId, location.pathname, navigate, toast, t]);

  const paymentDetailVerifierName = paymentVerifierDisplayName(selectedPayment);

  const handleOpenRelatedBill = (billId) => {
    if (!billId) return;
    setShowPaymentModal(false);
    setSelectedPayment(null);
    navigate('/bills', { state: { openBillId: billId } });
  };

  const handleOpenRelatedClient = (clientId) => {
    if (!clientId) return;
    setShowPaymentModal(false);
    setShowRejectModal(false);
    setShowApproveModal(false);
    setPaymentToApprove(null);
    setSelectedPayment(null);
    navigate('/clients', { state: { openClientId: clientId } });
  };

  const handleOpenRelatedCertificate = (certificate) => {
    const id = certificate?.id;
    const certificateNumber = certificate?.certificateNumber;
    if (!id && !certificateNumber) return;
    setShowPaymentModal(false);
    setSelectedPayment(null);
    if (id) {
      navigate('/certificates', { state: { openCertificateId: id } });
    } else {
      navigate('/certificates', { state: { openCertificateNumber: certificateNumber } });
    }
  };

  const handleOpenApproveModal = (payment) => {
    setPaymentToApprove(payment);
    setShowApproveModal(true);
  };

  const handleCloseApproveModal = () => {
    if (approveLoading) return;
    setShowApproveModal(false);
    setPaymentToApprove(null);
  };

  const handleConfirmApprovePayment = async () => {
    if (!paymentToApprove?.id) return;
    setApproveLoading(true);
    try {
      await paymentsAPI.approve(paymentToApprove.id);
      toast.success(t('payments.paymentApproved'));
      setShowApproveModal(false);
      setPaymentToApprove(null);
      refetchPayments();
    } catch (error) {
      console.error('Error approving payment:', error);
      toast.error(error.message || t('payments.errorApprovingPayment'));
    } finally {
      setApproveLoading(false);
    }
  };

  const handleRejectPayment = (payment) => {
    setSelectedPayment(payment);
    setShowRejectModal(true);
    setRejectionReason('');
  };

  const handleRejectPaymentConfirm = async () => {
    if (!rejectionReason.trim()) {
      toast.error(t('payments.rejectionReasonRequired'));
      return;
    }

    try {
      await paymentsAPI.reject(selectedPayment.id, { reason: rejectionReason });
      toast.success(t('payments.paymentRejected'));
      setShowRejectModal(false);
      setSelectedPayment(null);
      setRejectionReason('');
      refetchPayments();
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast.error(error.message || t('payments.errorRejectingPayment'));
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'WAITING_FOR_VERIFICATION':
        return 'bg-orange-100 text-orange-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentMethodBadge = (method) => {
    const colors = {
      'CASH': 'bg-blue-100 text-blue-800',
      'CREDIT_CARD': 'bg-purple-100 text-purple-800',
      'WAVE': 'bg-orange-100 text-orange-800',
      'ORANGE_MONEY': 'bg-orange-100 text-orange-800',
      'BANK_TRANSFER': 'bg-gray-100 text-gray-800',
      'CREDIT': 'bg-teal-100 text-teal-800',
      'PREPAID': 'bg-emerald-100 text-emerald-800'
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  };

  const paymentMethodI18nKey = (method) => {
    if (!method) return 'payments.methods.cash';
    const k = String(method).toLowerCase().replace(/_/g, '');
    return `payments.methods.${k}`;
  };



  const columns = [
    {
      key: 'bill',
      title: t('bills.billNumber'),
      render: (value, row) => (
        <span className="font-mono text-sm">{row.bill?.billNumber || '-'}</span>
      )
    },
    // Client column - Hidden only for client users (single assigned client)
    ...(!isClient() ? [{
      key: 'client',
      title: t('client'),
      render: (value, row) => {
        const name = row.bill?.client?.companyName;
        const clientId = row.bill?.client?.id;
        if (!name) return '-';
        if (!clientId) return name;
        return (
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 hover:underline text-left"
            onClick={() => handleOpenRelatedClient(clientId)}
          >
            {name}
          </button>
        );
      }
    }] : []),
    {
      key: 'amount',
      title: t('amount'),
      render: (value) => (
        <span className="font-semibold">
          {new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF'
          }).format(value)}
        </span>
      )
    },
    {
      key: 'paymentMethod',
      title: t('payments.paymentMethod'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentMethodBadge(value)}`}>
          {t(paymentMethodI18nKey(value), { defaultValue: value })}
        </span>
      )
    },
    {
      key: 'status',
      title: t('status'),
      render: (value, row) => {
        const chips = getBillPaymentsTimelineChipsUpToRow(row);
        return (
          <div className="space-y-1">
            {chips.map((chip) => (
              <span
                key={chip.key}
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(chip.payment.status)}`}
              >
                {t(paymentStatusI18nKey(chip.payment, row.bill?.payments), {
                  defaultValue: chip.payment.status
                })}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      key: 'verifiedBy',
      title: t('payments.verifiedBy'),
      render: (_, row) => {
        const name = paymentVerifierDisplayName(row);
        return <span className="text-sm text-gray-700">{name || '—'}</span>;
      }
    },
    {
      key: 'processedDate',
      title: t('payments.processedDate'),
      render: (value, row) => (
        <span className="text-sm">
          {row.processedDate ? format(new Date(row.processedDate), 'dd/MM/yyyy HH:mm') : '-'}
        </span>
      )
    },
    {
      key: 'actions',
      title: t('actions'),
      sticky: true,
      width: '240px',
      minWidth: 240,
      render: (_, row) => (
        <div className="flex flex-nowrap items-center gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewPayment(row)}
            className="p-1"
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          {/* Approve/Reject buttons for accountants on waiting payments */}
          {isAccountant() && row.status === 'WAITING_FOR_VERIFICATION' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenApproveModal(row)}
                className="p-1 text-green-600 hover:text-green-700"
                title={t('payments.approvePayment')}
              >
                <CheckIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRejectPayment(row)}
                className="p-1 text-red-600 hover:text-red-700"
                title={t('payments.rejectPayment')}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </>
          )}
          {row.bill && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownloadReceipt(row.id)}
              className="p-1"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )
    }
  ];

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await paymentsAPI.getReceiptPdf(paymentId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const newWin = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWin) {
        toast.error(t('payments.popupBlockedForPdf'));
        window.URL.revokeObjectURL(url);
        return;
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success(t('payments.receiptDownloaded'));
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error(t('payments.errorDownloadingReceipt'));
    }
  };

  if (paymentsError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <CreditCardIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('payments.title')}</h1>
            <p className="text-gray-600">{paymentsPageDescription}</p>
          </div>
        </div>
        
        <div className="card">
          <div className="text-center text-red-600">
            <p>{t('payments.errorLoadingPayments')}</p>
            <Button onClick={refetchPayments} className="mt-2">
              {t('reports.retry')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <CreditCardIcon className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('payments.title')}</h1>
          <p className="text-gray-600">{paymentsPageDescription}</p>
        </div>
      </div>

      {/* Search — debounced; updates list automatically */}
      <div className="card">
        <Input
          placeholder={t('payments.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="w-full"
        />
      </div>

      {/* Filters — collapsible */}
      <div className="card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-medium text-gray-900">{t('reports.filters')}</h3>
          <Button
            type="button"
            variant="outline"
            className="shrink-0 inline-flex items-center justify-center gap-2"
            onClick={() => setPaymentFiltersOpen((o) => !o)}
            aria-expanded={paymentFiltersOpen}
            aria-controls="payments-filters-panel"
          >
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-600 transition-transform ${paymentFiltersOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
            {paymentFiltersOpen ? t('payments.hidePaymentFilters') : t('payments.showPaymentFilters')}
          </Button>
        </div>
        {paymentFiltersOpen && (
          <div id="payments-filters-panel" className="mt-4 space-y-4">
            <div className={`grid grid-cols-1 md:grid-cols-2 ${isClient() ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-4`}>
              {/* Client filter - Hidden only for client users */}
              {!isClient() && (
                <SearchableSelect
                  label={t('client')}
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  options={clientFilterOptions}
                  placeholder={t('certificates.allClients')}
                  disabled={clientsLoading}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('status')}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('payments.allStatuses')}</option>
                  <option value="PENDING">{t('payments.pending')}</option>
                  <option value="WAITING_FOR_VERIFICATION">{t('payments.waitingForVerification')}</option>
                  <option value="COMPLETED">{t('payments.completed')}</option>
                  <option value="FAILED">{t('payments.failed')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('payments.paymentMethod')}
                </label>
                <select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('payments.allMethods')}</option>
                  <option value="CASH">{t('payments.methods.cash')}</option>
                  <option value="CREDIT_CARD">{t('payments.methods.creditCard')}</option>
                  <option value="WAVE">{t('payments.methods.wave')}</option>
                  <option value="ORANGE_MONEY">{t('payments.methods.orangeMoney')}</option>
                  <option value="BANK_TRANSFER">{t('payments.methods.banktransfer')}</option>
                  <option value="CREDIT">{t('payments.methods.credit')}</option>
                  <option value="PREPAID">{t('payments.methods.prepaid')}</option>
                </select>
              </div>

              <Input
                type="date"
                label={t('reports.startDate')}
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />

              <Input
                type="date"
                label={t('reports.endDate')}
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>

            <div className="pt-4 border-t border-gray-100">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="checkbox-app"
                  checked={showCompletedPayments}
                  onChange={handleShowCompletedPaymentsChange}
                />
                <span className="text-sm text-gray-800">{t('payments.showCompletedPayments')}</span>
              </label>
            </div>

            <div>
              <Button variant="outline" onClick={clearFilters}>
                {t('reports.clearFilters')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="card">
        {paymentsLoading ? (
          <LoadingSpinner />
        ) : (
          <Table
            columns={columns}
            data={paymentsForTable}
            pagination={pagination}
            onPageChange={(page) => goToPage(page)}
          />
        )}
      </div>

      {/* Payment Details Modal */}
      {selectedPayment && (
        <Modal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          title={`${t('payments.paymentDetails')} - ${selectedPayment.bill?.billNumber}`}
          size="2xl"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('payments.paymentInformation')}</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>{t('payments.amount')}:</strong> {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(selectedPayment.amount)}</div>
                  <div><strong>{t('payments.paymentMethod')}:</strong> {t(paymentMethodI18nKey(selectedPayment.paymentMethod), { defaultValue: selectedPayment.paymentMethod })}</div>
                  <div><strong>{t('payments.transactionId')}:</strong> {selectedPayment.transactionId || '-'}</div>
                  <div><strong>{t('payments.processedDate')}:</strong> {format(new Date(selectedPayment.processedDate), 'dd/MM/yyyy HH:mm')}</div>
                  <div>
                    <strong>{t('status')}:</strong>
                    <div className="mt-1 space-y-1">
                      {getBillPaymentsTimelineChipsUpToRow(selectedPayment).map((chip) => (
                        <span
                          key={chip.key}
                          className={`mr-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(chip.payment.status)}`}
                        >
                          {t(paymentStatusI18nKey(chip.payment, selectedPayment.bill?.payments), {
                            defaultValue: chip.payment.status
                          })}
                        </span>
                      ))}
                    </div>
                  </div>
                  {paymentDetailVerifierName ? (
                    <div>
                      <strong>{t('payments.verifiedBy')}:</strong>{' '}
                      {paymentDetailVerifierName}
                    </div>
                  ) : null}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('bills.billInformation')}</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong>{t('bills.billNumber')}:</strong>{' '}
                    {selectedPayment.bill?.id ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 hover:underline font-mono"
                        onClick={() => handleOpenRelatedBill(selectedPayment.bill.id)}
                      >
                        {selectedPayment.bill?.billNumber}
                      </button>
                    ) : (
                      selectedPayment.bill?.billNumber
                    )}
                  </div>
                  <div>
                    <strong>{t('client')}:</strong>{' '}
                    {selectedPayment.bill?.client?.id ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={() => handleOpenRelatedClient(selectedPayment.bill.client.id)}
                      >
                        {selectedPayment.bill?.client?.companyName}
                      </button>
                    ) : (
                      selectedPayment.bill?.client?.companyName
                    )}
                  </div>
                  <div><strong>{t('loads.modal.ddpNumber')}:</strong> {selectedPayment.bill?.certificate?.ddpNumber || '-'}</div>
                  <div><strong>{t('bills.totalAmount')}:</strong> {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(selectedPayment.bill?.totalAmount || 0)}</div>
                  <div><strong>{t('bills.dueDate')}:</strong> {format(new Date(selectedPayment.bill?.dueDate), 'dd/MM/yyyy')}</div>
                </div>
              </div>

              {/* Certificate Information */}
              {selectedPayment.bill?.certificate && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{t('certificates.certificateInformation')}</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <strong>{t('certificates.certificateNumber')}:</strong>{' '}
                      {selectedPayment.bill?.certificate?.id ||
                      selectedPayment.bill?.certificate?.certificateNumber ? (
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-800 hover:underline font-mono"
                          onClick={() => handleOpenRelatedCertificate(selectedPayment.bill.certificate)}
                        >
                          {selectedPayment.bill.certificate.certificateNumber}
                        </button>
                      ) : (
                        selectedPayment.bill.certificate.certificateNumber
                      )}
                    </div>
                    <div><strong>{t('loads.modal.ddpNumber')}:</strong> {selectedPayment.bill.certificate.ddpNumber || '-'}</div>
                    <div><strong>{t('certificates.certificateDate')}:</strong> {format(new Date(selectedPayment.bill.certificate.date), 'dd/MM/yyyy')}</div>
                    <div><strong>{t('certificates.product')}:</strong> {selectedPayment.bill.certificate.product?.name}</div>
                    <div><strong>{t('certificates.totalLoads')}:</strong> {selectedPayment.bill.certificate.totalLoads}</div>
                    <div><strong>{t('certificates.totalWeight')}:</strong> {selectedPayment.bill.certificate.totalWeight ? (selectedPayment.bill.certificate.totalWeight / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : '-'} Ton</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}



      {/* Approve payment confirmation */}
      {paymentToApprove && (
        <Modal
          isOpen={showApproveModal}
          onClose={handleCloseApproveModal}
          title={t('payments.confirmApprovalTitle')}
          size="md"
        >
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="shrink-0 rounded-full bg-emerald-50 ring-1 ring-emerald-100 p-3">
                <CheckIcon className="h-6 w-6 text-emerald-700" aria-hidden />
              </div>
              <p className="text-sm text-gray-700 leading-relaxed pt-0.5">{t('payments.confirmApprovalPrompt')}</p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                {t('payments.paymentInformation')}
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">{t('bills.billNumber')}</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{paymentToApprove.bill?.billNumber || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('client')}</dt>
                  <dd className="font-medium text-gray-900">
                    {paymentToApprove.bill?.client?.id ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={() => handleOpenRelatedClient(paymentToApprove.bill.client.id)}
                      >
                        {paymentToApprove.bill?.client?.companyName || '—'}
                      </button>
                    ) : (
                      paymentToApprove.bill?.client?.companyName || '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('payments.amount')}</dt>
                  <dd className="font-semibold text-gray-900 tabular-nums">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(
                      paymentToApprove.amount || 0
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('payments.paymentMethod')}</dt>
                  <dd className="font-medium text-gray-900">
                    {t(paymentMethodI18nKey(paymentToApprove.paymentMethod), {
                      defaultValue: paymentToApprove.paymentMethod
                    })}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={handleCloseApproveModal} disabled={approveLoading}>
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="success"
                onClick={handleConfirmApprovePayment}
                loading={approveLoading}
              >
                <CheckIcon className="h-4 w-4 mr-2 shrink-0" />
                {t('payments.confirmApproveAction')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reject Payment Modal */}
      {selectedPayment && (
        <Modal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedPayment(null);
            setRejectionReason('');
          }}
          title={`${t('payments.rejectPayment')} - ${selectedPayment.bill?.billNumber}`}
          maxWidth="lg"
        >
          <div className="space-y-4">
            {/* Payment Information */}
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="font-semibold text-red-900 mb-2">{t('payments.paymentToReject')}</h4>
              <div className="space-y-1 text-sm text-red-800">
                <div><strong>{t('bills.billNumber')}:</strong> {selectedPayment.bill?.billNumber}</div>
                <div>
                  <strong>{t('client')}:</strong>{' '}
                  {selectedPayment.bill?.client?.id ? (
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={() => handleOpenRelatedClient(selectedPayment.bill.client.id)}
                    >
                      {selectedPayment.bill?.client?.companyName}
                    </button>
                  ) : (
                    selectedPayment.bill?.client?.companyName
                  )}
                </div>
                <div><strong>{t('loads.modal.ddpNumber')}:</strong> {selectedPayment.bill?.certificate?.ddpNumber || '-'}</div>
                <div><strong>{t('payments.amount')}:</strong> {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(selectedPayment.amount)}</div>
                <div><strong>{t('payments.paymentMethod')}:</strong> {t(paymentMethodI18nKey(selectedPayment.paymentMethod), { defaultValue: selectedPayment.paymentMethod })}</div>
              </div>
            </div>

            {/* Rejection Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('payments.rejectionReason')} *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t('payments.rejectionReasonPlaceholder')}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                rows="3"
                required
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedPayment(null);
                  setRejectionReason('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleRejectPaymentConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={!rejectionReason.trim()}
              >
                {t('payments.confirmReject')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PaymentsPage;